import { FC, useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  selectAgencyClients,
  selectAgencyLoading,
  selectAgencyAddLoading,
  selectAgencyAddError,
  selectAgencyCreateLoading,
  selectAgencyCreateError,
  selectAgencyRenameLoading,
  selectAgencyRenameError,
} from "../../store/selectors/agency";
import { selectAuthIsAgency, selectAuthOwnAccountId } from "../../store/selectors/auth";
import { fetchClients, addClient, detachClient, actAsClient, createClient, renameClient } from "../../store/agency/slice";
import { ManagedAccount } from "../../store/agency/types";
import TopNav from "../../components/TopNav/TopNav";
import Button from "../../components/Button/Button";
import Skeleton from "../../components/Skeleton/Skeleton";
import DetachConfirmModal from "./components/DetachConfirmModal/DetachConfirmModal";
import RenameClientModal from "./components/RenameClientModal/RenameClientModal";
import QuickStatsTable from "./components/QuickStatsTable/QuickStatsTable";
import Section from "../../components/Section/Section";
import AddClientModal from "./components/AddClientModal/AddClientModal";
import { AddClientTab } from "./components/AddClientPanel/types";
import { TabSearchIcon, PlusIcon } from "../../components/icons";

import styles from "./AgencyDashboard.module.css";

/** Reset form fields when an async operation transitions loading→done without error. */
function useResetOnSuccess(loading: boolean, error: string | null | undefined, onSuccess: () => void | (() => void)) {
  const prevLoading = useRef(false);
  useEffect(() => {
    let cleanup: void | (() => void) = undefined;
    if (prevLoading.current && !loading && !error) {
      cleanup = onSuccess();
    }
    prevLoading.current = loading;
    return typeof cleanup === "function" ? cleanup : undefined;
  }, [loading, error]); // onSuccess is stable (useCallback)
}

const AgencyDashboard: FC = () => {
  const dispatch = useAppDispatch();
  const isAgency = useAppSelector(selectAuthIsAgency);
  const accountId = useAppSelector(selectAuthOwnAccountId);
  const clients = useAppSelector(selectAgencyClients);
  const loading = useAppSelector(selectAgencyLoading);
  const addLoading = useAppSelector(selectAgencyAddLoading);
  const addError = useAppSelector(selectAgencyAddError);
  const createLoading = useAppSelector(selectAgencyCreateLoading);
  const createError = useAppSelector(selectAgencyCreateError);
  const renameLoading = useAppSelector(selectAgencyRenameLoading);
  const renameError = useAppSelector(selectAgencyRenameError);

  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [addTab, setAddTab] = useState<AddClientTab>("link");
  const [clientEmail, setClientEmail] = useState("");
  const [label, setLabel] = useState("");
  const [clientToDetach, setClientToDetach] = useState<ManagedAccount | null>(null);
  const [clientToRename, setClientToRename] = useState<ManagedAccount | null>(null);
  const [renameLabel, setRenameLabel] = useState("");

  const [createFirstName, setCreateFirstName] = useState("");
  const [createLastName, setCreateLastName] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [createPassword, setCreatePassword] = useState("");
  const [createLabel, setCreateLabel] = useState("");
  const [createSuccess, setCreateSuccess] = useState(false);

  useEffect(() => {
    if (accountId) {
      dispatch(fetchClients());
    }
  }, [dispatch, accountId]);

  const isClientsPending = Boolean(accountId) && (loading || clients === null);

  const handleCloseAddClientModal = useCallback(() => {
    setIsAddClientModalOpen(false);
    setClientEmail("");
    setLabel("");
    setCreateFirstName("");
    setCreateLastName("");
    setCreateEmail("");
    setCreatePassword("");
    setCreateLabel("");
    setAddTab("link");
  }, []);

  const handleOpenAddClientModal = useCallback(() => {
    setIsAddClientModalOpen(true);
  }, []);

  const handleAdd = useCallback(() => {
    if (!clientEmail.trim()) return;
    dispatch(addClient({ clientEmail: clientEmail.trim(), label: label.trim() || undefined }));
  }, [dispatch, clientEmail, label]);

  const resetAddForm = useCallback(() => {
    setClientEmail("");
    setLabel("");
    handleCloseAddClientModal();
  }, [handleCloseAddClientModal]);

  useResetOnSuccess(addLoading, addError, resetAddForm);

  const handleCreate = useCallback(() => {
    if (!createEmail.trim() || !createFirstName.trim() || !createLastName.trim() || !createPassword.trim()) return;
    dispatch(createClient({
      email: createEmail.trim(),
      firstName: createFirstName.trim(),
      lastName: createLastName.trim(),
      password: createPassword,
      label: createLabel.trim() || undefined,
    }));
  }, [dispatch, createEmail, createFirstName, createLastName, createPassword, createLabel]);

  const resetCreateForm = useCallback(() => {
    setCreateFirstName("");
    setCreateLastName("");
    setCreateEmail("");
    setCreatePassword("");
    setCreateLabel("");
    handleCloseAddClientModal();
    setCreateSuccess(true);
    const timer = setTimeout(() => setCreateSuccess(false), 4000);
    return () => clearTimeout(timer);
  }, [handleCloseAddClientModal]);

  useResetOnSuccess(createLoading, createError, resetCreateForm);

  const handleDetach = useCallback((managed: ManagedAccount) => {
    setClientToDetach(managed);
  }, []);

  const handleRename = useCallback((managed: ManagedAccount) => {
    setClientToRename(managed);
    setRenameLabel(managed.label ?? "");
  }, []);

  const handleCloseRenameModal = useCallback(() => {
    setClientToRename(null);
    setRenameLabel("");
  }, []);

  const handleConfirmRename = useCallback(() => {
    if (!clientToRename || !renameLabel.trim()) return;
    dispatch(renameClient({
      managedId: clientToRename.id,
      label: renameLabel.trim(),
    }));
  }, [dispatch, clientToRename, renameLabel]);

  const resetRenameForm = useCallback(() => {
    handleCloseRenameModal();
  }, [handleCloseRenameModal]);

  useResetOnSuccess(renameLoading, renameError, resetRenameForm);

  const handleConfirmDetach = useCallback(() => {
    if (clientToDetach) {
      dispatch(detachClient({ managedId: clientToDetach.id }));
      setClientToDetach(null);
    }
  }, [dispatch, clientToDetach]);

  const handleActAs = useCallback((managed: ManagedAccount) => {
    dispatch(actAsClient({
      clientId: managed.clientOwnerId,
      clientAccountId: managed.clientAccountId,
      label: managed.label,
      email: managed.clientEmail,
    }));
  }, [dispatch]);

  const isCreateValid = Boolean(
    createEmail.trim() && createFirstName.trim() && createLastName.trim() && createPassword.length >= 8,
  );

  const activeClients = clients?.filter((c) => c.status === "active") ?? [];
  const detachedClients = clients?.filter((c) => c.status === "detached") ?? [];
  const hasClients = (clients?.length ?? 0) > 0;

  const renderEmptyMessage = (message: string) => (
    <p className={styles["agency__empty"]}>{message}</p>
  );

  return (
    <>
      <TopNav title="Agency" />
      <div className={styles["agency"]}>
        {!isClientsPending && !hasClients && (
          <p className={styles["agency__description"]}>
            Manage client accounts from one dashboard. Add your first client to get started.
          </p>
        )}

        {createSuccess && (
          <p className={styles["agency__success"]}>Client created and linked successfully.</p>
        )}

        <Section
          icon={<TabSearchIcon />}
          title="Clients"
          headerRight={
            <Button
              type="button"
              label="Add Client"
              typeStyle="outline-green"
              Icon={PlusIcon}
              size="sm"
              onClick={handleOpenAddClientModal}
            />
          }
        >
          {isClientsPending && (
            <div className={styles["agency__loading"]}>
              <Skeleton height={48} className={styles["agency__skeleton-row"]} />
              <Skeleton height={48} className={styles["agency__skeleton-row"]} />
              <Skeleton height={48} className={styles["agency__skeleton-row"]} />
            </div>
          )}

          {!isClientsPending && activeClients.length === 0 && renderEmptyMessage(
            isAgency
              ? "No managed clients yet. Use Add Client to link your first account."
              : "Add your first client to start managing accounts from one dashboard.",
          )}

          {!isClientsPending && activeClients.length > 0 && (
            <QuickStatsTable
              clients={activeClients}
              onActAs={handleActAs}
              onRename={handleRename}
              onDetach={handleDetach}
            />
          )}
        </Section>

        {!isClientsPending && detachedClients.length > 0 && (
          <Section icon={<TabSearchIcon />} title="Detached">
            <QuickStatsTable clients={detachedClients} />
          </Section>
        )}
      </div>

      <AddClientModal
        isOpen={isAddClientModalOpen}
        onClose={handleCloseAddClientModal}
        addTab={addTab}
        onTabChange={setAddTab}
        clientEmail={clientEmail}
        onClientEmailChange={setClientEmail}
        label={label}
        onLabelChange={setLabel}
        onLinkSubmit={handleAdd}
        addLoading={addLoading}
        addError={addError}
        createFirstName={createFirstName}
        onCreateFirstNameChange={setCreateFirstName}
        createLastName={createLastName}
        onCreateLastNameChange={setCreateLastName}
        createEmail={createEmail}
        onCreateEmailChange={setCreateEmail}
        createPassword={createPassword}
        onCreatePasswordChange={setCreatePassword}
        createLabel={createLabel}
        onCreateLabelChange={setCreateLabel}
        onCreateSubmit={handleCreate}
        createLoading={createLoading}
        createError={createError}
        isCreateValid={isCreateValid}
      />

      <DetachConfirmModal
        client={clientToDetach}
        onConfirm={handleConfirmDetach}
        onClose={() => setClientToDetach(null)}
      />

      <RenameClientModal
        client={clientToRename}
        label={renameLabel}
        onLabelChange={setRenameLabel}
        onConfirm={handleConfirmRename}
        onClose={handleCloseRenameModal}
        loading={renameLoading}
        error={renameError}
      />
    </>
  );
};

export default AgencyDashboard;
